import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useMount } from 'react-use';
import { useSelector } from 'react-redux';
import { format as formatDate } from 'date-fns';
import { Flex, Box, Text, Heading } from 'roo-ui/components';
import { getCreditCardErrors } from 'store/booking/bookingSelectors';
import { getIsCreditCardValid, getPayableNowCashAmount, getPayableLaterCashAmount } from 'store/checkout/checkoutSelectors';
import Currency from 'components/Currency';
import TextButton from 'components/TextButton';
import MastercardForm from './MastercardForm';
import { getPayableLaterDueDate } from 'store/quote/quoteSelectors';
import { DISPLAY_DATE_FORMAT } from 'config';
import BookingError from 'pages/Checkout/BookingError';
import MastercardDetails from './MastercardDetails';

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const EditButton = ({ setEditing, ...rest }) => (
  <TextButton onClick={() => setEditing(true)} textDecoration="none" fontWeight="bold" {...rest}>
    EDIT CARD DETAILS
  </TextButton>
);

EditButton.propTypes = {
  setEditing: PropTypes.func.isRequired,
};

const CreditCard = () => {
  const payableNowCashAmount = useSelector(getPayableNowCashAmount);
  const payableLaterCashAmount = useSelector(getPayableLaterCashAmount);
  const payableLaterDueDate = useSelector(getPayableLaterDueDate);
  const isCreditCardValid = useSelector(getIsCreditCardValid);
  const creditCardErrors = useSelector(getCreditCardErrors);
  const [isEditing, setEditing] = useState(false);

  useMount(() => {
    !isCreditCardValid && setEditing(true);
  });

  return (
    <Fragment>
      <Box mb={8}>
        <Flex justifyContent="space-between">
          <Heading.h4 fontSize="md" fontWeight="normal">
            {isEditing ? 'Enter your' : 'Your'} credit card details.
          </Heading.h4>
          {!isEditing && (
            <Box display={['none', 'block']}>
              <EditButton setEditing={setEditing} data-testid="edit-button-desktop" />
            </Box>
          )}
        </Flex>
        <Text>You&apos;ll be charged </Text>
        <Currency amount={payableNowCashAmount} currency="AUD" fontWeight="bold" data-testid="payable-now-cash-amount" />
        {payableLaterCashAmount.greaterThan(0) && (
          <Fragment>
            <Text fontWeight="bold"> now</Text>
            <Text> and </Text>
            <Currency amount={payableLaterCashAmount} currency="AUD" fontWeight="bold" data-testid="payable-later-cash-amount" />
            <Text data-testid="payable-later-due-date"> on {formatDate(payableLaterDueDate, DISPLAY_DATE_FORMAT)}</Text>
          </Fragment>
        )}
      </Box>
      <BookingError errors={creditCardErrors} type="credit card" />
      {isEditing && <MastercardForm />}
      {!isEditing && (
        <Fragment>
          <MastercardDetails />
          <Box pt={4} display={['block', 'none']}>
            <EditButton setEditing={setEditing} data-testid="edit-button-phone" />
          </Box>
        </Fragment>
      )}
    </Fragment>
  );
};

export default CreditCard;
